import {
  IsString,
  IsNotEmpty,
  IsObject,
  IsEnum,
  IsOptional,
} from 'class-validator';
import {
  ContentPerformanceGroupBy,
  ContentPerformanceMetric,
  PostStatusFilter,
} from './analytics-types';

/**
 * Report type enum for saved reports
 */
export enum ReportType {
  CONTENT_PERFORMANCE = 'Content Performance',
  CONTENT_DETAIL = 'Content Detail',
  INFLUENCER_ACTIVITY = 'Influencer Activity',
  INDIVIDUAL_INFLUENCER = 'Individual Influencer',
}

/**
 * Report tab types for content performance report
 */
export enum ReportTab {
  SUMMARY = 'summary',
  DOWNLOAD = 'download',
  BOOKMARK = 'bookmark',
}

/**
 * Chart type options
 */
export type ChartType = 'line' | 'bar' | 'column';

/**
 * Serializable grid state for saved reports (sort + column filters).
 * Mirrors the subset of Kendo's State type that we persist.
 */
export interface SavedGridState {
  sort?: { field: string; dir: 'asc' | 'desc' }[];
  filter?: {
    logic: 'and' | 'or';
    filters: Record<string, unknown>[];
  };
}

/** Filters for Content Performance report. No influencerGroup. */
export interface ContentPerformanceReportFilters {
  startDate: string;
  endDate: string;
  postTypes?: string[];
  topics?: string[];
  analystIds?: number[];
  firmIds?: number[];
  status?: PostStatusFilter;
}

/** Display options for Content Performance. selectedTab is required. */
export interface ContentPerformanceReportDisplayOptions {
  groupBy?: ContentPerformanceGroupBy;
  selectedMetric?: ContentPerformanceMetric;
  chartType?: ChartType;
  selectedTab: ReportTab;
}

export interface ContentPerformanceReportConfiguration {
  filters: ContentPerformanceReportFilters;
  displayOptions: ContentPerformanceReportDisplayOptions;
  /** Ordered list of visible grid column field names. */
  gridColumns?: string[];
  /** Persisted grid sort and column filter state. */
  gridState?: SavedGridState;
}

// ========== Content Detail (strict config) ==========

/** Filters for Content Detail report. postId is required; no influencerGroup string. */
export interface ContentDetailReportFilters {
  postId: string;
  startDate: string;
  endDate: string;
  influencerGroupId?: number | null;
  influencerIds?: number[];
  firmIds?: number[];
}

export interface ContentDetailReportConfiguration {
  filters: ContentDetailReportFilters;
  /** Ordered list of visible grid column field names. */
  gridColumns?: string[];
  /** Persisted grid sort and column filter state. */
  gridState?: SavedGridState;
}

// ========== Influencer Activity (strict config) ==========

/** Filters for Influencer Activity report */
export interface InfluencerActivityReportFilters {
  startDate: string;
  endDate: string;
  influencerGroupId?: number | null;
  influencerTier?: number;
  influencerIds?: number[];
  firmIds?: number[];
}

/** Display options for Influencer Activity */
export interface InfluencerActivityReportDisplayOptions {
  groupBy?: string;
  selectedMetric?: string;
  chartType?: ChartType;
}

export interface InfluencerActivityReportConfiguration {
  filters: InfluencerActivityReportFilters;
  displayOptions: InfluencerActivityReportDisplayOptions;
  /** Ordered list of visible grid column field names. */
  gridColumns?: string[];
  /** Persisted grid sort and column filter state. */
  gridState?: SavedGridState;
}

// ========== Individual Influencer (strict config) ==========

/** Filters for Individual Influencer report. influencerId only; no influencerGroup. */
export interface IndividualInfluencerReportFilters {
  startDate: string;
  endDate: string;
  influencerId: number;
}

/** Display options for Individual Influencer. No selectedTab. */
export interface IndividualInfluencerReportDisplayOptions {
  chartType?: ChartType;
}

export interface IndividualInfluencerReportConfiguration {
  filters: IndividualInfluencerReportFilters;
  displayOptions: IndividualInfluencerReportDisplayOptions;
  /** Ordered list of visible grid column field names. */
  gridColumns?: string[];
  /** Persisted grid sort and column filter state. */
  gridState?: SavedGridState;
}

// ========== Union and legacy aliases ==========

/**
 * Saved report configuration (discriminated by report type when creating/updating).
 */
export type SavedReportConfiguration =
  | ContentPerformanceReportConfiguration
  | ContentDetailReportConfiguration
  | InfluencerActivityReportConfiguration
  | IndividualInfluencerReportConfiguration;

/** @deprecated Use ContentPerformanceReportFilters or the specific report filters. */
export type SavedReportFilters =
  | ContentPerformanceReportFilters
  | ContentDetailReportFilters
  | InfluencerActivityReportFilters
  | IndividualInfluencerReportFilters;

/**
 * Saved report interface
 */
export interface SavedReport {
  id: string;
  reportName: string;
  dateCreated: Date;
  lastUpdated: Date;
  type: ReportType;
  creator: string;
  siteId: string;
  configuration: SavedReportConfiguration;
}

/**
 * DTO for creating a saved report
 */
export class CreateSavedReportDto {
  @IsString()
  @IsNotEmpty()
  reportName: string;

  @IsEnum(ReportType)
  type: ReportType;

  @IsObject()
  configuration: SavedReportConfiguration;
}

/**
 * DTO for updating a saved report
 */
export class UpdateSavedReportDto {
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  reportName?: string;

  @IsOptional()
  @IsObject()
  configuration?: SavedReportConfiguration;
}
